<template>
  <v-data-iterator
    :items="items"
    :search="search"
    :items-per-page="itemsPerPage"
    :footer-props="footerProps"
    :hide-default-footer="hideDefaultFooter || loading || items.length === 0"
    :loading="loading"
    :item-key="itemKey"
    :sort-by="sortByField"
    :sort-desc="sortDescField"
  >
    <template #loading>
      <div class="mdl-empty">
        <v-icon class="mdl-empty__icon mdi-spin">mdi-loading</v-icon>
        <span class="mdl-empty__text">Laddar data...</span>
      </div>
    </template>

    <template #no-data>
      <div class="mdl-empty">
        <v-icon class="mdl-empty__icon">mdi-music-note</v-icon>
        <span class="mdl-empty__text">Här är det tomt.</span>
      </div>
    </template>

    <template v-if="showSelect || sortableHeaders.length > 0" #header>
      <div class="mdl-header" :class="{ 'mdl-header--has-tertiary': tertiaryHeader }">
        <v-simple-checkbox
          v-if="showSelect"
          color="primary"
          :value="allSelected"
          :indeterminate="someSelected"
          @input="toggleAll"
        />
        <v-select
          v-if="sortableHeaders.length > 0"
          v-model="sortByField"
          :items="sortableHeaders"
          item-text="text"
          item-value="value"
          :placeholder="$vuetify.lang.t('$vuetify.dataTable.sortBy')"
          :menu-props="{ contentClass: 'mdl-sort-menu' }"
          clearable
          filled
          rounded
          hide-details
          class="mdl-header__sort"
        />
        <span v-if="showNumSelected && value.length > 0" class="mdl-header__label">{{ value.length }} valda</span>
        <v-btn v-if="sortByField" icon small @click="sortDescField = !sortDescField">
          <v-icon small>{{ sortDescField ? 'mdi-sort-descending' : 'mdi-sort-ascending' }}</v-icon>
        </v-btn>
      </div>
    </template>

    <template #default="{ items: pageItems }">
      <div class="mdl-list" style="padding-top: 4px">
        <div
          v-for="item in pageItems"
          :key="item[itemKey]"
          class="mdl-item"
        >
          <div class="mdl-item__row" @click="toggleExpand(item)">
            <div v-if="showSelect || hasIconSlot" class="mdl-item__col1" @click.stop>
              <v-simple-checkbox
                v-if="showSelect"
                color="primary"
                :value="isSelected(item)"
                @input="toggleSelect(item)"
              />
              <slot v-else name="item-icon" :item="item" />
            </div>
            <div class="mdl-item__col2">
              <div class="mdl-item__primary">
                <slot :name="`item.${primaryHeader.value}`" :item="item">
                  {{ item[primaryHeader.value] }}
                </slot>
              </div>
              <div v-if="secondaryHeader" class="mdl-item__secondary">
                <span class="mdl-item__secondary-label">{{ secondaryHeader.text }}: </span>
                <slot :name="`item.${secondaryHeader.value}`" :item="item">
                  {{ item[secondaryHeader.value] }}
                </slot>
              </div>
              <div v-if="tertiaryHeader" class="mdl-item__tertiary">
                <span class="mdl-item__secondary-label">{{ tertiaryHeader.text }}: </span>
                <slot :name="`item.${tertiaryHeader.value}`" :item="item">
                  {{ item[tertiaryHeader.value] }}
                </slot>
              </div>
            </div>
            <div class="mdl-item__col3">
              <v-icon small>{{ isExpanded(item) ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
            </div>
          </div>

          <div v-if="isExpanded(item)" class="mdl-item__expanded" :style="{ paddingLeft: expandedPaddingLeft }">
            <div v-for="h in expandedHeaders" :key="h.value" class="mdl-item__field" :class="{ 'mdl-item__field--stacked': h.stacked }">
              <span class="mdl-item__field-label">{{ h.text }}:</span>
              <span class="mdl-item__field-value">
                <slot :name="`item.${h.value}`" :item="item">
                  {{ item[h.value] }}
                </slot>
              </span>
            </div>
            <slot name="expanded-actions" :item="item" />
          </div>
        </div>
      </div>
    </template>
  </v-data-iterator>
</template>

<script>
/* eslint-disable */
export default {
  name: 'MobileDataList',

  props: {
    items:             { type: Array,   default: () => [] },
    headers:           { type: Array,   required: true },
    value:             { type: Array,   default: () => [] },   // v-model → selected items
    itemKey:           { type: String,  default: 'id' },
    showSelect:        { type: Boolean, default: false },
    singleExpand:      { type: Boolean, default: false },
    loading:           { type: Boolean, default: false },
    search:            { type: String,  default: '' },
    itemsPerPage:      { type: Number,  default: 10 },
    footerProps:       { type: Object,  default: () => ({}) },
    hideDefaultFooter:  { type: Boolean, default: false },
    showNumSelected:    { type: Boolean, default: false },
  },

  data: () => ({
    expandedKeys: [],
    sortByField: undefined,
    sortDescField: false,
  }),

  computed: {
    primaryHeader() {
      return this.headers.find(h => h.mobile === 'primary') || this.headers[0];
    },
    secondaryHeader() {
      return this.headers.find(h => h.mobile === 'secondary') || null;
    },
    tertiaryHeader() {
      return this.headers.find(h => h.mobile === 'tertiary') || null;
    },
    expandedHeaders() {
      return this.headers.filter(h => h.mobile !== 'primary' && h.mobile !== 'secondary' && h.mobile !== 'tertiary' && h.text);
    },
    allSelected() {
      return this.items.length > 0 && this.value.length === this.items.length;
    },
    someSelected() {
      return this.value.length > 0 && !this.allSelected;
    },
    hasIconSlot() {
      return !!this.$scopedSlots['item-icon'];
    },
    sortableHeaders() {
      return this.headers.filter(h => h.sortable !== false && h.text);
    },
    hasHeader() {
      return this.showSelect || this.sortableHeaders.length > 0;
    },
    expandedPaddingLeft() {
      if (this.showSelect) return '56px';
      if (this.hasIconSlot) return '48px';
      return '16px';
    },
  },

  methods: {
    isExpanded(item) {
      return this.expandedKeys.includes(item[this.itemKey]);
    },
    toggleExpand(item) {
      const key = item[this.itemKey];
      const expanding = !this.isExpanded(item);
      if (this.singleExpand) {
        this.expandedKeys = expanding ? [key] : [];
      } else {
        const idx = this.expandedKeys.indexOf(key);
        if (idx >= 0) this.expandedKeys.splice(idx, 1);
        else this.expandedKeys.push(key);
      }
      if (expanding) this.$emit('expand', item);
    },
    isSelected(item) {
      return this.value.some(s => s[this.itemKey] === item[this.itemKey]);
    },
    toggleSelect(item) {
      const next = this.isSelected(item)
        ? this.value.filter(s => s[this.itemKey] !== item[this.itemKey])
        : [...this.value, item];
      this.$emit('input', next);
    },
    toggleAll() {
      this.$emit('input', this.allSelected ? [] : [...this.items]);
    },
  },
};
</script>

<style scoped lang="scss">
.mdl-header {
  display: flex;
  align-items: center;
  padding: 0 8px 4px 16px;
  gap: 4px;

  &--has-tertiary {
    margin-bottom: 8px;
  }

  &__sort {
    flex: 1;
    min-width: 0;

    ::v-deep .v-input__slot {
      background-color: var(--color-secondary-container) !important;
    }
  }

  &__label {
    font-size: 14px;
    color: var(--color-on-surface-variant);
    margin-left: 4px;
  }
}

.mdl-list {
  padding-bottom: 8px;
}

.mdl-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 180px;
}

.mdl-empty__icon {
  font-size: 48px !important;
  color: var(--color-on-surface-variant);
  opacity: 0.4;
}

.mdl-empty__text {
  margin-top: 12px;
  font-size: 14px;
  color: var(--color-on-surface-variant);
  opacity: 0.6;
}

.mdl-item {
  &:not(:last-child) {
    margin-bottom: 24px;
  }
}

.mdl-item__row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 16px;
  cursor: pointer;
  min-height: 48px;
}

.mdl-item__col1 {
  flex: 0 0 auto;
}

.mdl-item__col2 {
  flex: 1;
  min-width: 0;
}

.mdl-item__primary {
  font-size: 16px;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mdl-item__secondary {
  font-size: 14px;
  color: var(--color-on-surface-variant);
  margin-top: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mdl-item__tertiary {
  font-size: 14px;
  color: var(--color-on-surface-variant);
  margin-top: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mdl-item__secondary-label {
  font-weight: 500;
}

.mdl-item__col3 {
  flex: 0 0 auto;
}

.mdl-item__expanded {
  padding: 8px 16px 0;
  margin-top: 4px;
}

.mdl-item__field {
  display: flex;
  gap: 8px;
  padding: 3px 0;
  font-size: 14px;
  line-height: 1.5;
}

.mdl-item__field--stacked {
  flex-direction: column;
  gap: 1px;
}

.mdl-item__field-label {
  flex: 0 0 auto;
  font-weight: 700;
  color: var(--color-on-surface-variant);
}

.mdl-item__field-value {
  flex: 1;
  min-width: 0;
}
</style>
